import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Paper, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, CircularProgress, IconButton, Tooltip, Chip, Tabs, Tab,
    Box, ThemeProvider, createTheme, Collapse, Card, CardContent, Divider, Alert, Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const theme = createTheme({
    palette: {
        primary: { main: '#11224d' },
        secondary: { main: '#E1AD01' },
        background: { default: '#f3f4f6', paper: '#fff' },
    },
    typography: { fontSize: 12 },
});

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5),
    backgroundColor: '#fff',
    borderRadius: theme.spacing(1),
}));

const StyledCard = styled(Card)(({ theme }) => ({
    marginTop: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
}));

// ---- Formatting helpers ----
const formatEmail = (email) => (email ? String(email).trim().toLowerCase() : '');

const formatCity = (city) => {
    if (!city) return '';
    const trimmed = String(city).trim().toLowerCase();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const formatDocket = (docket) => {
    if (!docket) return '';
    const digitsOnly = String(docket).replace(/[^0-9]/g, '');
    return digitsOnly ? `MC ${digitsOnly}` : String(docket);
};

const telHref = (phone) => {
    if (!phone) return '';
    const digits = String(phone).replace(/[^0-9+]/g, '');
    return `tel:${digits}`;
};

const ctaLinkSx = {
    color: '#4dabf5',
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
};

// Carrier/Broker header columns (unchanged from before)
const HEADER_COLUMNS = [
    { key: 'dot_number', label: 'DOT #', width: 100 },
    { key: 'legal_name', label: 'Legal Name', width: 260 },
    { key: 'phone', label: 'Phone', width: 150 },
    { key: 'email_address', label: 'Email', width: 240 },
];

// Shipper header columns — no phone column, since signup shippers never expose one.
const SHIPPER_HEADER_COLUMNS = [
    { key: 'company_name', label: 'Company Name', width: 260 },
    { key: 'phy_city', label: 'City', width: 160 },
    { key: 'phy_state', label: 'State', width: 90 },
    { key: 'email_address', label: 'Email', width: 240 },
];

const DetailSection = ({ title, children }) => (
    <Box sx={{ pr: 2 }}>
        <Typography
            variant="subtitle2"
            sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#11224d', mb: 0.5 }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

// --- Carrier/Broker favorite row (unchanged) ---
function FavoriteRow({ row, onRemove }) {
    const [open, setOpen] = useState(false);

    const email = formatEmail(row.email_address);
    const city = formatCity(row.phy_city);
    const docket = formatDocket(row.docket1);

    return (
        <>
            <TableRow
                hover
                onClick={() => setOpen(!open)}
                sx={{
                    cursor: 'pointer',
                    '& td': { position: 'relative' },
                    '& td:not(:last-child)::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '25%',
                        height: '50%',
                        borderRight: '1px solid #ddd',
                    },
                }}
            >
                <TableCell sx={{ textAlign: 'center' }}>{row.dot_number || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{row.legal_name || '-'}</TableCell>
                <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {row.phone ? (
                        <a href={telHref(row.phone)} style={ctaLinkSx}>{row.phone}</a>
                    ) : '-'}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {email ? (
                        <a href={`mailto:${email}`} style={ctaLinkSx}>{email}</a>
                    ) : '-'}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Remove from favorites">
                        <IconButton size="small" color="error" onClick={() => onRemove(row.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <StyledCard>
                            <CardContent sx={{ p: 1.5 }}>
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem', color: '#11224d' }}
                                >
                                    {row.legal_name || 'Unnamed Company'}
                                </Typography>
                                <Divider sx={{ my: 1 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ flex: 1, minWidth: 200, pr: 2, mb: 2 }}>
                                        <DetailSection title="Company">
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>DBA Name:</span>{' '}
                                                {row.dba_name || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>DOT Number:</span>{' '}
                                                {row.dot_number || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>MC #:</span>{' '}
                                                {docket || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Power Units:</span>{' '}
                                                {row.power_units ?? '-'}
                                            </Typography>
                                        </DetailSection>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 200, pr: 2, mb: 2 }}>
                                        <DetailSection title="Contact">
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Phone:</span>{' '}
                                                {row.phone ? (
                                                    <a href={telHref(row.phone)} style={ctaLinkSx}>{row.phone}</a>
                                                ) : '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Fax:</span>{' '}
                                                {row.fax || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Email:</span>{' '}
                                                {email ? (
                                                    <a href={`mailto:${email}`} style={ctaLinkSx}>{email}</a>
                                                ) : '-'}
                                            </Typography>
                                        </DetailSection>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 200, mb: 2 }}>
                                        <DetailSection title="Location">
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>City:</span>{' '}
                                                {city || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>State:</span>{' '}
                                                {row.phy_state || '-'}
                                            </Typography>
                                        </DetailSection>
                                    </Box>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

// --- Shipper favorite row (new) ---
// row fields come from the favorites table's shipper columns:
// company_name, shipper_source ('csv' | 'signup'), phy_street, phy_city,
// phy_state, phy_zip, email_address. Phone intentionally never shown here,
// matching the same privacy rule used in SearchShippers.
function ShipperFavoriteRow({ row, onRemove }) {
    const [open, setOpen] = useState(false);
    const email = formatEmail(row.email_address);
    const city = formatCity(row.phy_city);
    const isSignup = row.shipper_source === 'signup';

    return (
        <>
            <TableRow
                hover
                onClick={() => setOpen(!open)}
                sx={{
                    cursor: 'pointer',
                    '& td': { position: 'relative' },
                    '& td:not(:last-child)::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '25%',
                        height: '50%',
                        borderRight: '1px solid #ddd',
                    },
                }}
            >
                <TableCell sx={{ fontWeight: 'bold' }}>
                    {row.company_name || '-'}
                    {isSignup && (
                        <Chip label="Verified" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                    )}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{city || '-'}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{row.phy_state || '-'}</TableCell>
                <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {email ? <a href={`mailto:${email}`} style={ctaLinkSx}>{email}</a> : '-'}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Remove from favorites">
                        <IconButton size="small" color="error" onClick={() => onRemove(row.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <StyledCard>
                            <CardContent sx={{ p: 1.5 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem', color: '#11224d' }}>
                                    {row.company_name || 'Unnamed Company'}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ flex: 1, minWidth: 200, pr: 2, mb: 2 }}>
                                        <DetailSection title="Company">
                                            {!isSignup && (
                                                <Typography variant="body2">
                                                    <span style={{ fontWeight: 'bold' }}>Industry:</span>{' '}
                                                    {row.shipper_industry || '-'}
                                                </Typography>
                                            )}
                                            {!isSignup && (
                                                <Typography variant="body2">
                                                    <span style={{ fontWeight: 'bold' }}>Website:</span>{' '}
                                                    {row.shipper_website ? (
                                                        <a href={row.shipper_website} target="_blank" rel="noreferrer" style={ctaLinkSx}>
                                                            {row.shipper_website}
                                                        </a>
                                                    ) : '-'}
                                                </Typography>
                                            )}
                                            {!isSignup && (
                                                <Typography variant="body2">
                                                    <span style={{ fontWeight: 'bold' }}>Contact Name:</span>{' '}
                                                    {row.shipper_contact_name || '-'}
                                                </Typography>
                                            )}
                                            {isSignup && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Registered ShipperDock account
                                                </Typography>
                                            )}
                                        </DetailSection>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 200, pr: 2, mb: 2 }}>
                                        <DetailSection title="Contact">
                                            {/* Phone intentionally omitted for signup shippers — only CSV-posted shippers show it */}
                                            {/* {!isSignup && (
                                                <Typography variant="body2">
                                                    <span style={{ fontWeight: 'bold' }}>Phone:</span>{' '}
                                                    {row.shipper_phone ? (
                                                        <a href={telHref(row.shipper_phone)} style={ctaLinkSx}>{row.shipper_phone}</a>
                                                    ) : '-'}
                                                </Typography>
                                            )} */}
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Email:</span>{' '}
                                                {email ? <a href={`mailto:${email}`} style={ctaLinkSx}>{email}</a> : '-'}
                                            </Typography>
                                        </DetailSection>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 200, mb: 2 }}>
                                        <DetailSection title="Location">
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Street:</span>{' '}
                                                {row.phy_street || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>City:</span>{' '}
                                                {city || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>State:</span>{' '}
                                                {row.phy_state || '-'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <span style={{ fontWeight: 'bold' }}>Zip:</span>{' '}
                                                {row.phy_zip || '-'}
                                            </Typography>
                                        </DetailSection>
                                    </Box>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

/**
 * FavoritesViewer — shared by Broker and Carrier dashboards.
 * userRole: 'broker' | 'carrier'
 *   - broker sees tabs: Carriers, Shippers
 *   - carrier sees tabs: Brokers, Shippers
 */
const FavoritesViewer = ({ userRole }) => {
    const defaultTab = userRole === 'broker' ? 'carrier' : 'broker';
    const [tab, setTab] = useState(defaultTab);
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const authHeaders = () => {
        const token = localStorage.getItem('jwtToken');
        return { Authorization: `Bearer ${token}` };
    };

    const fetchFavorites = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/api/favorites`, {
                headers: authHeaders(),
                params: { type: tab }
            });
            setFavorites(res.data);
        } catch (err) {
            console.error('Failed to fetch favorites', err);
            setSnackbar({ open: true, message: 'Could not load favorites.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => {
        fetchFavorites();
    }, [fetchFavorites]);

    const handleRemove = async (id) => {
        try {
            await axios.delete(`${API_BASE}/api/favorites/${id}`, { headers: authHeaders() });
            setFavorites(prev => prev.filter(f => f.id !== id));
            setSnackbar({ open: true, message: 'Removed from favorites.', severity: 'success' });
        } catch (err) {
            console.error('Failed to remove favorite', err);
            setSnackbar({ open: true, message: 'Could not remove favorite.', severity: 'error' });
        }
    };

    const isShipperTab = tab === 'shipper';
    const columns = isShipperTab ? SHIPPER_HEADER_COLUMNS : HEADER_COLUMNS;

    const tabLabel = tab === 'carrier' ? 'Carriers' : tab === 'broker' ? 'Brokers' : 'Shippers';

    return (
        <ThemeProvider theme={theme}>
            <style>
                {`
                  html, body, #root {
                    margin: 0;
                    padding: 0;
                    height: 100%;
                    overflow: hidden;
                  }
                `}
            </style>
            <Box
                sx={{
                    backgroundColor: '#f3f4f6',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Container
                    maxWidth="xl"
                    disableGutters
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        height: '100%',
                        overflow: 'hidden',
                        pl: 1,
                        pr: 1,
                        mt: 0,
                    }}
                >
                    <StyledPaper elevation={1} sx={{ mt: 1, mb: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#11224d', mb: 1 }}>
                            Favorites
                        </Typography>
                        <Tabs
                            value={tab}
                            onChange={(e, v) => setTab(v)}
                            sx={{
                                minHeight: 36,
                                '& .MuiTab-root': { minHeight: 36, fontWeight: 600, fontSize: '0.8rem' },
                            }}
                        >
                            {userRole === 'broker' && <Tab label="Carriers" value="carrier" />}
                            {userRole === 'carrier' && <Tab label="Brokers" value="broker" />}
                            <Tab label="Shippers" value="shipper" />
                        </Tabs>
                    </StyledPaper>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            py: 1,
                            borderTop: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: '#f9fafc',
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {favorites.length} Saved
                        </Typography>
                        {favorites.length > 0 && (
                            <Chip label={tabLabel} size="small" color="primary" variant="outlined" />
                        )}
                    </Box>

                    {loading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : favorites.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">No saved {tabLabel.toLowerCase()} yet.</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                            <TableContainer component={Paper} sx={{ maxHeight: '100%', overflowX: 'auto', overflowY: 'auto' }}>
                                <Table
                                    size="small"
                                    sx={{
                                        tableLayout: 'fixed',
                                        '& .MuiTableCell-root': {
                                            padding: '7px 11px',
                                            fontSize: '0.7rem',
                                            lineHeight: 1.2,
                                        },
                                    }}
                                >
                                    <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#11224d' }}>
                                        <TableRow>
                                            {columns.map((col) => (
                                                <TableCell
                                                    key={col.key}
                                                    sx={{
                                                        backgroundColor: '#11224d',
                                                        color: 'white',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 'bold',
                                                        padding: '6px 10px',
                                                        whiteSpace: 'nowrap',
                                                        width: col.width,
                                                        maxWidth: col.width,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        textAlign: 'center',
                                                        borderBottom: 'none',
                                                    }}
                                                >
                                                    {col.label}
                                                </TableCell>
                                            ))}
                                            <TableCell
                                                sx={{
                                                    backgroundColor: '#11224d',
                                                    color: 'white',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 'bold',
                                                    padding: '6px 10px',
                                                    whiteSpace: 'nowrap',
                                                    textAlign: 'center',
                                                    borderBottom: 'none',
                                                }}
                                            >
                                                Remove
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {favorites.map((row) => (
                                            isShipperTab
                                                ? <ShipperFavoriteRow key={row.id} row={row} onRemove={handleRemove} />
                                                : <FavoriteRow key={row.id} row={row} onRemove={handleRemove} />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} sx={{ width: '100%' }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default FavoritesViewer;